import { useState } from 'react';
import './SolutionsPage.css';


function normalizeText(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

const solutionKeywords = {
    'tanga dashboard': [
        'tableau',
        'tableau de bord',
        'dashboard',
        'administration',
        'gestion',
        'pilotage',
        'statistiques',
    ],

    aestheticare: [
        'esthetique',
        'beaute',
        'soins',
        'clients',
        'rendez vous',
    ],
};

function editDistance(first, second) {
    const table = Array.from(
        { length: first.length + 1 },
        () => Array(second.length + 1).fill(0)
    );

    for (let i = 0; i <= first.length; i++) table[i][0] = i;
    for (let j = 0; j <= second.length; j++) table[0][j] = j;

    for (let i = 1; i <= first.length; i++) {
        for (let j = 1; j <= second.length; j++) {
            const cost = first[i - 1] === second[j - 1] ? 0 : 1;

            table[i][j] = Math.min(
                table[i - 1][j] + 1,
                table[i][j - 1] + 1,
                table[i - 1][j - 1] + cost
            );
        }
    }

    return table[first.length][second.length];
}

function matchesSearch(text, query) {
    if (!query || text.includes(query)) return true;

    const queryWords = query.split(' ').filter(Boolean);
    const textWords = text.split(' ').filter(Boolean);

    return queryWords.every((word) =>
        textWords.some((candidate) => {
            const maxErrors =
                word.length >= 7 ? 2 :
                    word.length >= 4 ? 1 : 0;

            return candidate.startsWith(word) ||
                (
                    maxErrors > 0 &&
                    Math.abs(candidate.length - word.length) <= maxErrors &&
                    editDistance(candidate, word) <= maxErrors
                );
        })
    );
}



const emptyForm = {
    name: '',
    url: '',
    description: '',
    category: 'Gestion',
};

function SolutionsPage({ solutions, setSolutions }) {

    const [formData, setFormData] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState('');


    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const resetForm = () => {
        setFormData(emptyForm);
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const normalizedUrl = formData.url.trim();

        if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl)) {
            alert('Le lien doit commencer par https:// ou http://.');
            return;
        }

        const savedSolution = {
            ...formData,
            name: formData.name.trim(),
            url: normalizedUrl,
            description: formData.description.trim(),
        };

        if (editingId !== null) {
            setSolutions((previous) =>
                previous.map((solution) =>
                    solution.id === editingId
                        ? { ...solution, ...savedSolution }
                        : solution
                )
            );
        } else {
            setSolutions((previous) => [
                ...previous,
                { id: Date.now(), ...savedSolution },
            ]);
        }

        resetForm();
    };


    const handleEdit = (solution) => {
        setFormData({
            name: solution.name || '',
            url: solution.url || '',
            description: solution.description || '',
            category: solution.category || 'Gestion',
        });

        setEditingId(solution.id);
        setShowForm(true);
    };


    const handleDelete = (id) => {
        const confirmed = window.confirm(
            'Voulez-vous vraiment supprimer cette solution ?'
        );

        if (!confirmed) return;

        setSolutions((previous) =>
            previous.filter((solution) => solution.id !== id)
        );
    };


    const handleCopyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            alert('Lien copié avec succès !');
        } catch (error) {
            window.prompt('Copiez ce lien manuellement :', url);
        }
    };



    const filteredSolutions = solutions.filter((solution) => {
        const query = normalizeText(search);

        const name = normalizeText(solution.name);
        const category = normalizeText(solution.category);

        const keywords = solutionKeywords[name] || [];

        const searchableText = normalizeText([
            solution.name,
            solution.category,
            solution.description,
            solution.url,
            ...keywords,
        ].join(' '));

        return (
            !query ||
            matchesSearch(searchableText, query) ||
            matchesSearch(name, query) ||
            matchesSearch(category, query)
        );
    });


    return (
        <div className="solutions-page">
            <div className="solutions-intro">
                <div>
                    <p className="solutions-eyebrow">ESPACE ADMINISTRATION</p>
                    <h2>Solutions digitales</h2>
                    <p className="solutions-subtitle">
                        Centralisez les liens des plateformes de Tanga Africa.
                    </p>
                </div>

                <button
                    type="button"
                    className="solutions-primary-button"
                    onClick={() => {
                        if (showForm) {
                            resetForm();
                        } else {
                            setFormData(emptyForm);
                            setEditingId(null);
                            setShowForm(true);
                        }
                    }}
                >
                    {showForm ? 'Annuler' : '+ Ajouter une solution'}
                </button>
            </div>

            <div className="solutions-stats">
                <div className="solutions-stat-card">
                    <span className="solutions-stat-icon">🧩</span>
                    <div>
                        <p>Total des solutions</p>
                        <strong>{solutions.length}</strong>
                    </div>
                </div>

                <div className="solutions-stat-card">
                    <span className="solutions-stat-icon">🔗</span>
                    <div>
                        <p>Liens configurés</p>
                        <strong>
                            {solutions.filter((solution) => solution.url.trim()).length}
                        </strong>
                    </div>
                </div>
            </div>


            {showForm && (
                <div
                    className="solutions-modal-overlay"
                    onClick={resetForm}
                >
                    <section
                        className="solutions-form-card solutions-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="solutions-modal-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="solutions-modal-close"
                            onClick={resetForm}
                            aria-label="Fermer la fenêtre"
                        >
                            ×
                        </button>

                        <h3 id="solutions-modal-title">
                            {editingId !== null
                                ? 'Modifier une solution'
                                : 'Ajouter une nouvelle solution'}
                        </h3>

                        <p className="solutions-form-description">
                            Renseignez les informations de la plateforme ci-dessous.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div className="solutions-form-grid">
                                <div className="solutions-field">
                                    <label htmlFor="solution-name">
                                        Nom de la solution *
                                    </label>
                                    <input
                                        id="solution-name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Ex. Tanga Dashboard"
                                        required
                                    />
                                </div>

                                <div className="solutions-field">
                                    <label htmlFor="solution-category">
                                        Catégorie *
                                    </label>
                                    <select
                                        id="solution-category"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="Gestion">Gestion</option>
                                        <option value="Sécurité">Sécurité</option>
                                        <option value="Métier">Métier</option>
                                        <option value="Autre">Autre</option>
                                    </select>
                                </div>

                                <div className="solutions-field solutions-field-full">
                                    <label htmlFor="solution-url">
                                        Adresse du site
                                    </label>
                                    <input
                                        id="solution-url"
                                        type="url"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleChange}
                                        placeholder="https://exemple.com"
                                    />
                                    <small>
                                        Le lien est facultatif : tu pourras le renseigner plus tard.
                                    </small>
                                </div>

                                <div className="solutions-field solutions-field-full">
                                    <label htmlFor="solution-description">
                                        Description *
                                    </label>
                                    <textarea
                                        id="solution-description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Expliquez à quoi sert cette plateforme."
                                        rows="3"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="solutions-form-actions">
                                <button
                                    type="button"
                                    className="solutions-cancel-button"
                                    onClick={resetForm}
                                >
                                    Annuler
                                </button>

                                <button
                                    type="submit"
                                    className="solutions-primary-button"
                                >
                                    {editingId !== null
                                        ? 'Enregistrer les modifications'
                                        : 'Enregistrer la solution'}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            )}

            <section className="solutions-list-section">
                <div className="solutions-list-heading">
                    <div>
                        <h3>Liste des solutions</h3>
                        <p>Retrouvez et gérez toutes les plateformes enregistrées.</p>
                    </div>

                    <input
                        className="solutions-search"
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Rechercher une solution..."
                        aria-label="Rechercher une solution"
                    />
                </div>

                {filteredSolutions.length === 0 ? (
                    <div className="solutions-empty">
                        <span>🔎</span>
                        <h3>Aucune solution trouvée</h3>
                        <p>Essayez un autre mot-clé ou ajoutez une nouvelle solution.</p>
                    </div>
                ) : (
                    <div className="solutions-cards">
                        {filteredSolutions.map((solution) => (
                            <article className="solutions-item-card" key={solution.id}>
                                <div className="solutions-item-top">
                                    <div className="solutions-item-icon">🌐</div>
                                    <span className="solutions-category">
                                        {solution.category}
                                    </span>
                                </div>

                                <h3>{solution.name}</h3>
                                <p className="solutions-item-description">
                                    {solution.description}
                                </p>


                                <div className="solutions-url-row">
                                    <p className="solutions-item-url">
                                        {solution.url || 'Lien non configuré'}
                                    </p>

                                    {solution.url && (
                                        <button
                                            type="button"
                                            className="solutions-copy-button"
                                            onClick={() => handleCopyLink(solution.url)}
                                            aria-label="Copier le lien"
                                            title="Copier le lien"
                                        >
                                            <svg
                                                width="22"
                                                height="22"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                            >
                                                <rect x="8" y="8" width="13" height="13" rx="3" />
                                                <path d="M16 8V5a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h3" />
                                            </svg>
                                        </button>
                                    )}
                                </div>


                                <div className="solutions-item-actions">
                                    {solution.url ? (
                                        <a
                                            className="solutions-open-link"
                                            href={solution.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Ouvrir ↗
                                        </a>
                                    ) : (
                                        <span className="solutions-link-pending">
                                            Lien à configurer
                                        </span>
                                    )}


                                    <button
                                        type="button"
                                        className="solutions-edit-button"
                                        onClick={() => handleEdit(solution)}
                                    >
                                        Modifier
                                    </button>

                                    <button
                                        type="button"
                                        className="solutions-delete-button"
                                        onClick={() => handleDelete(solution.id)}
                                    >
                                        Supprimer
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

export default SolutionsPage;